import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PAGE_SIZE_OPTIONS } from "../../constants/kpiConstants";

/**
 * Reusable pagination component for KPI pages
 */
export default function KPIPagination({
  pagination,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) {
  if (pagination.totalPages <= 1) return null;

  const pages = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(
    1,
    pagination.currentPage - Math.floor(maxVisiblePages / 2)
  );
  let endPage = Math.min(
    pagination.totalPages,
    startPage + maxVisiblePages - 1
  );

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between mt-6 px-4 py-3 bg-white rounded shadow gap-4">
      {/* Results info and page size selector */}
      <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
        <span className="text-sm text-gray-700 text-center sm:text-left">
          Showing{" "}
          {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.count
          )}{" "}
          of {pagination.count} results
        </span>

        <select
          value={pagination.pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <button
          onClick={() => onPageChange(pagination.currentPage - 1)}
          disabled={!pagination.hasPrevious || loading}
          className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeft size={20} />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-sm"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded border text-sm ${
              page === pagination.currentPage
                ? "bg-blue-500 text-white border-blue-500"
                : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < pagination.totalPages && (
          <>
            {endPage < pagination.totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => onPageChange(pagination.totalPages)}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-sm"
            >
              {pagination.totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(pagination.currentPage + 1)}
          disabled={!pagination.hasNext || loading}
          className="p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}